import type { SiteContentHead, SiteVersionContent } from "../../../lib/api";
import type { FieldAttributes } from "./types";
import type { EditorManifestV3, SiteSourceOperation } from "./manifestV3";

export function buildSourceEditDocument(input: {
  manifest: EditorManifestV3; buildId: string | null; head: SiteContentHead | null;
  sourceBuildId?: string; sourceBundleHash?: string;
  values: Record<string, string>; attributes: FieldAttributes; imageAssetIds: Record<string, string>;
  styles: Record<string, string>; changedFields: Iterable<string>; changedStyles: Iterable<string>;
}): Extract<SiteVersionContent, { schemaVersion: 3 }> {
  const { manifest, buildId, head, values, attributes, imageAssetIds, styles } = input;
  if (!buildId) throw new Error("A source build is required before saving this site.");
  const operations: SiteSourceOperation[] = [];
  for (const path of input.changedFields) {
    const field = manifest.fields.find(candidate => candidate.path === path);
    if (!field) continue;
    const metadata = attributes[path];
    const assetId = imageAssetIds[path] ?? field.metadata?.assetId;
    if (field.type === "image" && assetId) {
      operations.push({ op: "set-image", path, assetId,
        ...(metadata?.alt !== undefined ? { alt: metadata.alt } : {}),
        ...(metadata?.focalX !== undefined || field.metadata?.focalX !== undefined ? { focalX: metadata?.focalX ?? field.metadata?.focalX } : {}),
        ...(metadata?.focalY !== undefined || field.metadata?.focalY !== undefined ? { focalY: metadata?.focalY ?? field.metadata?.focalY } : {}),
      });
    } else operations.push({ op: "set-field", path, value: values[path] ?? field.value,
      capability: field.type === "image" ? "src" : field.source.attribute });
    for (const capability of ["href", "alt"] as const) {
      // set-image already owns its alt change. Claiming that attribute again
      // is rejected atomically by the imported-source worker.
      if (capability === "alt" && field.type === "image" && assetId) continue;
      if (metadata?.[capability] !== undefined && (field.capabilities ?? []).includes(capability))
        operations.push({ op: "set-field", path, value: metadata[capability]!, capability });
    }
  }
  for (const name of input.changedStyles) {
    const font = manifest.styles.fonts.find(item => `font.${item.id}` === name);
    if (font) operations.push({ op: "set-font", id: font.id, family: styles[name], cssFamily: styles[name], ...(font.stylesheetUrl ? { stylesheetUrl: font.stylesheetUrl } : {}) });
    else if (manifest.styles.tokens.some(token => token.name === name)) operations.push({ op: "set-token", name, value: styles[name] });
  }
  const prior = head?.content.schemaVersion === 3 && (head.buildId === buildId || !head.buildId) ? head.content : undefined;
  const sourceBuildId = input.sourceBuildId ?? prior?.sourceBuildId ?? buildId;
  const sourceBundleHash = input.sourceBundleHash ?? prior?.sourceBundleHash;
  return { schemaVersion: 3, sourceBuildId, ...(sourceBundleHash ? { sourceBundleHash } : {}), fields: {}, operations };
}

export function savedSourceStyles(content?: SiteVersionContent): Record<string, string> {
  if (content?.schemaVersion !== 3) return {};
  return Object.fromEntries(content.operations.flatMap(operation => operation.op === "set-token"
    ? [[operation.name, operation.value]] : operation.op === "set-font" ? [[`font.${operation.id}`, operation.cssFamily]] : []));
}
